import { Dispatch, SetStateAction, useState } from 'react';

import { useAuthQuery } from '../../../../auth/hooks/useAuthQuery';
import { TABS } from '../../../../constants';
import { useSmartPooling } from '../../../../hooks/useSmartPooling';
import { useTabContentHeight } from '../../../../hooks/useTabContentHeight';
import { FareDetailsTab } from '../../../../rides/features/RideOverview/FareDetailsTab';
import { RideOverviewTab } from '../../../../rides/features/RideOverview/RideOverviewTab';
import { SmartPoolingTab } from '../../../../rides/features/RideOverview/SmartPoolingTab';
import { SmartPoolingBanner } from '../../../components/SmartPoolingBanner';
import { useRideOverviewStore } from '../../../stores/rideOverviewStore';

interface UseRideOverviewTabsHook {
  tabs: {
    value: string;
    tabLabel: string;
    hidden: boolean;
    disabled: boolean;
    dataTestId: string;
    scrollHeight: string;
    Component: () => JSX.Element;
  }[];
  currentTab: string;
  setCurrentTab: Dispatch<SetStateAction<string>>;
  baseHeight: string;
  errorText?: string;
}

export const useRideOverviewTabs = (
  isSeries?: boolean,
  formData?: Partial<RideFormData> | null
): UseRideOverviewTabsHook => {
  const [currentTab, setCurrentTab] = useState(TABS.rideOverview);
  const { userFeatures } = useAuthQuery();
  const { rideOverview, ridersOnRide } = useRideOverviewStore();
  const { tabHeight: overviewHeight, onTabContentChange: overviewCallback } = useTabContentHeight();
  const { tabHeight: smartPoolingHeight, onTabContentChange: smartPoolingCallback } = useTabContentHeight();
  const { tabHeight: fareDetailsHeight, onTabContentChange: fareDetailsCallback } = useTabContentHeight();

  if (currentTab === TABS.fareDetails && rideOverview.rideInfo?.distance === undefined) {
    setCurrentTab(TABS.rideOverview);
  }

  const {
    hasSmartPoolingRequirements,
    handleSearchRideSuggestions,
    isSearching,
    disableSearch,
    suggestions,
    handleSuggestionClick,
  } = useSmartPooling(formData, ridersOnRide);

  const rideInfo = rideOverview?.rideInfo;

  const showAdditionalInfo = rideOverview?.rideInfo?.distance && rideOverview.rideInfo?.distance >= 0;
  const baseHeight =
    showAdditionalInfo && !userFeatures?.enableSmartpooling && !rideInfo?.perRiderFareDetails ? '200px' : 'auto';

  const showSmartPoolingTab = userFeatures?.enableSmartpooling && !rideOverview?.rideId && isSeries;

  const tabs = [
    {
      value: TABS.rideOverview,
      tabLabel: 'Ride Overview',
      hidden: false,
      disabled: false,
      dataTestId: 'ride-overview-tab',
      scrollHeight: overviewHeight,
      Component: () => (
        <div ref={overviewCallback} style={{ width: '100%' }}>
          {userFeatures?.enableSmartpooling && hasSmartPoolingRequirements && (
            <SmartPoolingBanner
              handleFindSuggestions={() => {
                setCurrentTab(TABS.smartPooling);
                !disableSearch && handleSearchRideSuggestions();
              }}
            />
          )}
          <RideOverviewTab
            ridersCount={rideOverview?.riderCount || 0}
            boosters={rideOverview?.boosterCount || 0}
            rideInfo={rideInfo}
            driverInfo={rideOverview?.driverInfo}
          />
        </div>
      ),
    },
    {
      value: TABS.smartPooling,
      tabLabel: 'SmartPooling',
      hidden: !showSmartPoolingTab,
      disabled: false,
      dataTestId: 'smartpooling-tab',
      scrollHeight: smartPoolingHeight,
      Component: () => (
        <div ref={smartPoolingCallback} style={{ width: '100%' }}>
          <SmartPoolingTab
            enableFindSuggestions={!disableSearch}
            onFindSuggestions={handleSearchRideSuggestions}
            isSearching={isSearching}
            suggestions={suggestions}
            handleSuggestionClick={handleSuggestionClick}
          />
        </div>
      ),
    },
    {
      value: TABS.fareDetails,
      tabLabel: 'Fare Details',
      hidden: false,
      disabled:
        (!rideInfo?.fareDetails && !rideInfo?.perRiderFareDetails) || (!rideInfo?.distance && !rideInfo?.duration),
      dataTestId: 'fare-details-tab',
      scrollHeight: fareDetailsHeight,
      Component: () => (
        <div ref={fareDetailsCallback} style={{ width: '100%' }}>
          {rideInfo && <FareDetailsTab rideInfo={rideInfo} />}
        </div>
      ),
    },
  ];

  const errorText = rideOverview?.rideInfo?.errorText;

  return { tabs, currentTab, setCurrentTab, baseHeight, errorText };
};
